<script setup lang="ts">
import { ref } from 'vue'
import { storeToRefs } from 'pinia'
import { useUserCreditsStore } from '../stores/user-credits.store'
import { useNotification } from '@/composables/useNotification'
import PurchaseCreditsDialog from './PurchaseCreditsDialog.vue'
import type { PurchaseCreditCommand } from '../types/user-credits.types'

// Global host for the Add-Credits purchase dialog so the top-nav "Add Credits" button can open
// it from any page (Vue 2 had the button in the nav). The dialog visibility + price packages
// live in the user-credits store; this component owns the Stripe checkout window + polling that
// used to live in UserCreditsView.
const store = useUserCreditsStore()
const { pricePackages, purchaseDialogOpen } = storeToRefs(store)
const { success: notifySuccess, error: notifyError } = useNotification()

// True while a checkout is in flight (creating the session, Stripe tab open, polling). Drives the
// dialog's "Processing your payment..." view; clearing it returns the dialog to package selection.
const checkoutInProgress = ref(false)

async function handlePurchase(command: PurchaseCreditCommand): Promise<void> {
	// Open the tab synchronously inside the click handler so the pop-up blocker allows it.
	const win = window.open('', '_blank')
	checkoutInProgress.value = true

	let windowWatch: ReturnType<typeof setInterval> | undefined

	try {
		const result = await store.purchaseCredits(command)
		if (!result) {
			win?.close()
			notifyError('Failed to initiate purchase')
			return
		}

		if (win) {
			win.location.href = result.url
		}

		// If the user closes the Stripe tab without paying, the session stays "open" and polling would
		// otherwise spin until the 30-minute cap, leaving the dialog stuck on "Processing...". Watch the
		// tab and stop waiting as soon as it is closed.
		windowWatch = setInterval(() => {
			if (win && win.closed) store.cancelPolling()
		}, 1000)

		const status = await store.pollCheckoutSession(result.checkoutSessionId)

		// User aborted (Cancel button or closed the Stripe tab): keep the dialog open — the finally
		// clears checkoutInProgress, returning it to the selection view so they can retry.
		if (status === 'cancelled') return

		purchaseDialogOpen.value = false

		if (status === 'complete') {
			notifySuccess('Payment successful! Credits will be updated shortly.')
		} else if (status === 'expired') {
			notifyError('Payment session expired')
		} else if (status === 'error') {
			notifyError('Unable to verify payment status')
		}
	} finally {
		if (windowWatch) clearInterval(windowWatch)
		checkoutInProgress.value = false
	}
}

// Cancel (or the X) while a payment is in progress: stop polling. pollCheckoutSession then resolves
// 'cancelled' and handlePurchase's finally clears checkoutInProgress.
function handleCancel(): void {
	store.cancelPolling()
}
</script>

<template>
	<PurchaseCreditsDialog
		v-model="purchaseDialogOpen"
		:price-packages="pricePackages"
		:processing="checkoutInProgress"
		@purchase="handlePurchase"
		@cancel="handleCancel"
	/>
</template>
